import { ButtonComposition, CollapseComposition } from '@codeleap/web'
import { textVariant } from './Text'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'

export type NavigationComposition =
  | 'section'
  | `collapse${Capitalize<CollapseComposition>}`
  | `title${Capitalize<ButtonComposition>}`
  | `item${Capitalize<ButtonComposition>}`
  | 'titleWrapper:collapsible'
  | 'titleText:collapsible'
  | `collapseWrapper:collapsible`
  | 'itemWrapper:focused'
  | 'itemText:focused'
  | 'itemIcon:focused'
  | 'group'

const createNavigationVariant = createStyles<NavigationComposition>

export const NavigationStyles = {
  default: createNavigationVariant(theme => ({
    section: {
      ...theme.presets.column,
    },
    collapseWrapper: {
      ...theme.presets.column,
      ...theme.presets.fullWidth,
      ...theme.effects.light,
    },
    titleWrapper: {
      backgroundColor: 'transparent',
      cursor: 'default',
      transition: 'all .2s ease',
      ...theme.spacing.marginBottom(1),
      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid0,
      },
    },
    'titleWrapper:collapsible': {
      cursor: 'pointer',

      '&:hover': {
        background: theme.baseColors.neutralSolid200,
      },
    },
    titleIcon: {
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
    },
    titleText: {
      ...textVariant('p3'),
    },
    itemWrapper: {
      backgroundColor: theme.baseColors.neutralSolid0,
      ...theme.presets.fullWidth,
      ...theme.presets.justifyStart,
      textDecoration: 'none',
      borderRadius: 0,
      ...theme.spacing.padding(2.5),

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid300,
      },

      '&:last-of-type': {
        borderBottomWidth: 0,
      },
    },
  })),
  list: createNavigationVariant(theme => ({
    section: {
      alignItems: 'flex-start',

      [theme.media.up('tabletSmall')]: {
        padding: theme.spacing.value(0),
        margin: theme.spacing.value(0),
      },

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        flex: 1,
      },
    },
    collapseWrapper: {
      borderRadius: theme.radius.regular,

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.row,
      },
    },
    group: {
      borderRadius: theme.radius.regular,
      overflow: 'hidden',
      ...theme.presets.column,

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.row,
      },
    },
    'collapseWrapper:collapsible': {
      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.column,
      },
    },
    titleText: {
      ...textVariant('p2'),
    },
    itemWrapper: {
      backgroundColor: theme.baseColors.neutralSolid200,
      ...theme.presets.center,
      borderRadius: 0,
      ...theme.spacing.padding(2.5),
      textDecoration: 'none',
      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid300,
      },

      [theme.media.down('tabletSmall')]: {
        flex: 1,
        borderWidth: 0,
      },
    },
    'itemWrapper:focused': {
      background: theme.baseColors.neutralSolid300,

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid400,
      },
    },
  })),
  'lastItem:collapsible': createNavigationVariant(theme => ({
    itemWrapper: {
      borderBottomRightRadius: theme.radius.regular,
      borderBottomLeftRadius: theme.radius.regular,
    },
  })),
  menu: createNavigationVariant(theme => ({
    section: {
      ...theme.presets.alignStart,
      ...theme.presets.justifySpaceBetween,

      [theme.media.up('tabletSmall')]: {
        borderRadius: theme.radius.regular,
        padding: theme.spacing.value(0),
        margin: theme.spacing.value(0),
      },

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        flex: 1,
      },
    },
    collapseWrapper: {
      borderRadius: theme.radius.regular,

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        ...theme.presets.row,
        ...theme.border({
          color: theme.baseColors.neutralSolid200,
          directions: ['bottom'],
          width: 1,
        }),
      },
    },
    'collapseWrapper:collapsible': {
      [theme.media.up('tabletSmall')]: {
        ...theme.effects.light,
        borderRadius: theme.radius.regular,
      },

      [theme.media.down('tabletSmall')]: {
        ...theme.effects.light,
        ...theme.presets.column,
      },
    },
    group: {
      borderRadius: theme.radius.regular,

      overflow: 'hidden',
      ...theme.presets.column,

      [theme.media.up('tabletSmall')]: {
        ...theme.effects.light,
      },

      [theme.media.down('tabletSmall')]: {
        ...theme.presets.fullWidth,
        borderRadius: 0,
        ...theme.presets.row,
        ...theme.border({
          color: theme.baseColors.neutralSolid200,
          directions: ['bottom'],
          width: 1,
        }),
      },
    },
    titleWrapper: {
      ...theme.border({ color: theme.baseColors.primarySolid300, width: 0 }),
      ...theme.spacing.marginTop(2),
      ...theme.presets.fullWidth,
    },
    titleText: {
      ...textVariant('p3'),
    },
    'titleText:collapsible': {
      textAlign: 'start',
    },
    itemWrapper: {
      backgroundColor: theme.baseColors.neutralSolid0,
      borderRadius: 0,
      ...theme.border({ color: theme.baseColors.primarySolid300, width: 0 }),
      ...theme.spacing.padding(2.5),
      textDecoration: 'none',
      ...theme.spacing.gap(2),

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid0,
        filter: 'brightness(95%)',
      },

      [theme.media.down('tabletSmall')]: {
        flex: 1,
      },
    },
    'itemWrapper:focused': {
      [theme.media.down('tabletSmall')]: {
        flex: 1,
      },
    },
    itemText: {
      ...theme.presets.fullWidth,
      textAlign: 'start',
      color: theme.baseColors.neutralSolid1000,
    },
    'itemText:focused': {
      color: theme.baseColors.primarySolid300,
    },
    itemIcon: {
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
    },
    'itemIcon:focused': {
      color: theme.baseColors.primarySolid300,
    },
  })),
}

StyleRegistry.registerVariants('Navigation', NavigationStyles)
